import { LLMMergeFieldValidation } from './LLMMergeFieldValidation';
import { useTranslation } from 'react-i18next';
import { ChangePromptData, LLMPrompt } from './LLMPrompt';
import { LLMProviderSelect } from './LLMProviderSelect';
import { StyledLoader } from './styles';
import {
  LLMDecisionData,
  LLMErrors,
  MergeFieldValidationOption,
  ModelOption,
} from './types';
import { FC, useCallback } from 'react';
import { useProviderOptions } from './useProviderOptions';
import { LLMConfidenceThreshold } from './LLMConfidenceThreshold';

type LLMDecisionProps = {
  llmDecision: LLMDecisionData;
  mergeFields: string[];
  onChange: (data: Partial<LLMDecisionData>) => void;
  errors: LLMErrors;
};

export const LLMDecision: FC<LLMDecisionProps> = ({
  llmDecision: {
    modelName,
    prompt,
    htmlPrompt,
    mergeFieldValidation,
    confidenceThreshold,
    belowThresholdAction,
  },
  errors,
  mergeFields,
  onChange,
}) => {
  const { t } = useTranslation();

  const { isProvidersLoading, providerOptions, model } = useProviderOptions({
    modelName,
    isForDecision: true,
  });

  const setModelName = useCallback(
    (model: ModelOption | undefined) => {
      onChange({
        modelName: model?.value,
        businessPluginAppId: model?.model?.business_plugin_app_id,
      });
    },
    [onChange]
  );

  const onChangePrompt = useCallback(
    ({ htmlPrompt, prompt }: ChangePromptData) => {
      onChange({
        htmlPrompt,
        prompt,
      });
    },
    [onChange]
  );

  const onChangeMergeFieldValidation = useCallback(
    ({ value }: MergeFieldValidationOption) => {
      onChange({
        mergeFieldValidation: value,
      });
    },
    [onChange]
  );

  return (
    <StyledLoader loading={isProvidersLoading}>
      <LLMProviderSelect
        value={model}
        onChange={setModelName}
        options={providerOptions}
        errors={errors}
        disabled={false}
      />
      <LLMPrompt
        prompt={prompt}
        htmlPrompt={htmlPrompt}
        mergeFields={mergeFields}
        model={model}
        noProviderOptions={!providerOptions.length}
        disabled={!model}
        onChange={onChangePrompt}
        placeholder={t(
          'Based on context, is the customer showing purchase intent?'
        )}
        isDecision
      />
      <LLMMergeFieldValidation
        value={mergeFieldValidation}
        onChange={onChangeMergeFieldValidation}
        disabled={!model}
      />
      <LLMConfidenceThreshold
        confidenceThreshold={confidenceThreshold}
        belowThresholdAction={belowThresholdAction}
        onChange={onChange}
        disabled={!model}
      />
    </StyledLoader>
  );
};
